import React, { memo, useState, useEffect } from 'react'
import { BouncingLoader } from 'examkrackers-components'

import styled from 'styled-components'
import StaticSideNav from 'components/StaticSideNav'
import { CalendarContextProvider } from 'hooks/CalendarContext'
import CalendarWidget from 'modules/dashboard/components/CalendarWidget'
import { useSelector } from 'react-redux'
import { getStudent } from 'modules/auth/ducks/selectors'
import SatlyGreeting from '../../public/assets/illustrations/SaltyGreeting.svg'
import { BookProgressChartWidget } from './BookProgressChartWidget'
import WidgetTutoringWeeklySchedule from 'modules/tutoring/components/WidgetTutoringWeeklySchedule'
import { useTranslation } from 'react-i18next'
import StepsForSuccessWidget from 'modules/onboarding/components/StepsForSuccessWidget'
import NextTasks from 'modules/dashboard/tasksWidget/NextTasks'

import PATHS from 'utils/paths'
import AiWidget from 'modules/dashboard/aiWidget/AiWidget'
import AnalyticsService from 'services/AnalyticsService'
import { ANALYTICS_EVENTS } from 'utils/analytics'

import {
  getAiTutorEnabledInCourse,
  getCurrentCourse
} from 'modules/courses/ducks/selectors'
import { Course } from 'types/courses'
import { isNotNilOrEmpty } from 'utils/ramda'
import { useHistory } from 'react-router-dom'
import { RootReducer } from '../types'
import * as R from 'ramda'
import useIsMobile from 'hooks/useIsMobile'
import { COURSE_TYPES } from 'utils/courses'
import { FreeTrialFeaturedExamsWidget } from 'modules/freeTrial/components/FreeTrialFeaturedExamsWidget'

const MemoizedCalendar = memo(CalendarWidget)

const NewDashboard = () => {
  const isMobile = useIsMobile()
  const { t } = useTranslation()
  const { push } = useHistory()
  const [seeDashboardPage, setSeeDashboardPage] = useState(true)
  const studentProfile = useSelector(getStudent)
  const currentCourse: Course = useSelector(getCurrentCourse)
  const dashboardSettings = currentCourse.original?.dashboard_settings || '{}'
  const dashboardSettingsObject = JSON.parse(dashboardSettings)
  const isFreeTrial = R.pipe(
    R.propOr('', 'type'),
    R.equals(COURSE_TYPES.freeTrial)
  )(currentCourse)

  const hasCourseFetched = R.pipe(
    R.propOr('', 'id'),
    isNotNilOrEmpty
  )(currentCourse)

  const aiTutorEnabledInCourse = useSelector((state: RootReducer) =>
    getAiTutorEnabledInCourse(state)
  )
  const isCalendarEnabled = currentCourse.original?.is_calendar_enabled || false
  const isBookProgressWidgetEnabled =
    dashboardSettingsObject?.['books_progress_enabled'] || false
  const isTutorsWidgetEnabled =
    dashboardSettingsObject?.['tutoring_enabled'] || false
  const isStepsForSuccessWidgetEnabled =
    dashboardSettingsObject?.['steps_for_success_enabled'] || false

  const isDashboardAvailable =
    isCalendarEnabled ||
    isBookProgressWidgetEnabled ||
    isTutorsWidgetEnabled ||
    isStepsForSuccessWidgetEnabled ||
    aiTutorEnabledInCourse

  const { name } = studentProfile
  const [_, lastName] = name.split(' ')

  useEffect(() => {
    if (isNotNilOrEmpty(currentCourse.id) && seeDashboardPage) {
      AnalyticsService(studentProfile).logEvent(
        ANALYTICS_EVENTS.seeDashboardPage,
        {
          'Course type': currentCourse?.type || false,
          'Course name': currentCourse?.title || false,
          'Course expiration date': currentCourse?.accessible_to || false
        }
      )
      setSeeDashboardPage(false)
    }
  }, [currentCourse, seeDashboardPage])

  useEffect(() => {
    if (isNotNilOrEmpty(currentCourse.id) && !isDashboardAvailable) {
      push(PATHS.exams)
    }
  }, [currentCourse])

  useEffect(() => {
    if (isMobile) {
      push(PATHS.books)
    }
  }, [isMobile])

  if (!hasCourseFetched) {
    return (
      <LoaderContainer>
        <BouncingLoader />
      </LoaderContainer>
    )
  }

  return (
    <>
      <DashboardWrapper>
        <DashboardBackground />
        <CalendarContextProvider>
          <Header>
            <div className='header-content'>
              <h1>{t('dashboard.welcome', { lastName })}</h1>
              <img src={SatlyGreeting} alt='Salty Greeting' />
            </div>
            {isFreeTrial && <FreeTrialFeaturedExamsWidget />}
          </Header>
          <DashboardContainer>
            <NavContainer>
              <StaticSideNav />
            </NavContainer>
            <MainContent>
              <Content hasTwoColumns={isCalendarEnabled}>
                <ContentColumn>
                  {isStepsForSuccessWidgetEnabled && <StepsForSuccessWidget />}
                  {isCalendarEnabled && !isTutorsWidgetEnabled && (
                    <MemoizedCalendar />
                  )}
                  {isCalendarEnabled && isTutorsWidgetEnabled && <NextTasks />}
                </ContentColumn>
                <ContentColumn>
                  {isCalendarEnabled && isTutorsWidgetEnabled && (
                    <MemoizedCalendar />
                  )}
                  {isCalendarEnabled && !isTutorsWidgetEnabled && <NextTasks />}
                  {isTutorsWidgetEnabled && (
                    <Tutors>
                      <WidgetTutoringWeeklySchedule />
                    </Tutors>
                  )}
                </ContentColumn>
              </Content>
              {isBookProgressWidgetEnabled && <BookProgressChartWidget />}
              {aiTutorEnabledInCourse && <AiWidget />}
            </MainContent>
          </DashboardContainer>
        </CalendarContextProvider>
      </DashboardWrapper>
    </>
  )
}

export default NewDashboard

const DashboardBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100vw;
  height: 100%;
  background-image: url('/assets/illustrations/dashboard_hexagons.svg');
  background-repeat: no-repeat;
`

const DashboardWrapper = styled.div`
  overflow: visible;
  position: relative;
`

const DashboardContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
`

const NavContainer = styled.div`
  flex: none;
  width: 205px;
`

const MainContent = styled.div`
  flex: 1;
  padding-left: 20px;
`

const Header = styled.div`
  display: flex;
  padding-left: 48px;
  margin-bottom: 10px;
  width: 100%;
  align-items: flex-start;

  .header-content {
    display: flex;
    align-items: center;
  }

  h1 {
    font-family: 'Arial Black', Arial, sans-serif;
    font-size: 36px;
    font-weight: 900;
    line-height: 18px;
    color: ${({ theme }) => theme.colors.main.black};
    max-height: max-content;
    white-space: nowrap;
    max-width: 750px;
    overflow-x: clip;
    overflow-y: visible;
    text-overflow: ellipsis;
    z-index: 1;
  }

  img {
    z-index: 1;
  }
`

const Content = styled.div<{ hasTwoColumns?: boolean }>`
  display: grid;
  grid-template-columns: ${({ hasTwoColumns }) =>
    hasTwoColumns ? 'repeat(2, 1fr)' : '1fr'};
  gap: 20px;
`

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`

const Tutors = styled.div`
  margin-bottom: 20px;
`
const LoaderContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`
